<script setup lang="ts">
import { computed } from 'vue';
import { useLogto } from '@logto/vue';
import { session } from '../lib/session.ts';
import { demoAccounts as readDemoAccounts, rememberSwitch, type DemoAccount } from '../lib/demo-accounts.ts';

/**
 * One-click sign-in as each of the fixture accounts, for a demo deployment.
 *
 * It exists because "which address is the conductor again" is the friction
 * that stops people trying the thing you asked them to try. It renders
 * nothing at all unless the deployment names accounts in its own
 * configuration, so it cannot appear on a real deployment by accident.
 *
 * It is not a back door. Each button hands Logto a `login_hint` and nothing
 * else: Logto still asks for the password on its own page, under its own
 * rules, and this app never sees it.
 *
 * Switching accounts while signed in has to sign out first, and a sign-out is
 * a redirect — so the address to come back as is left in session storage and
 * picked up by the front page. See `App.vue`.
 */
const { signIn, signOut } = useLogto();
const origin = window.location.origin;

function signInAs(account: DemoAccount) {
  if (session.isAuthenticated) {
    // Comes back to the front page, which signs in again with the hint.
    rememberSwitch(account.email);
    signOut(`${origin}/`);
    return;
  }
  void signIn({ redirectUri: `${origin}/callback`, loginHint: account.email });
}

/** The panel exists only where the deployment names accounts. */
const accounts = computed(() => readDemoAccounts());
</script>

<template>
  <aside v-if="accounts.length" class="dev-signin">
    <p class="eyebrow">Testing accounts</p>
    <p class="meta">
      Sign in as one of the seeded people. You will still be asked for the shared
      password. This panel is only on the demo.
    </p>
    <p class="dev-signin__buttons">
      <button
        v-for="account in accounts"
        :key="account.email"
        class="btn"
        type="button"
        :title="account.email"
        @click="signInAs(account)"
      >{{ account.label }}</button>
    </p>
  </aside>
</template>

<style scoped>
.dev-signin {
  margin-top: var(--s-5);
  padding: var(--s-4);
  border: 1px dashed var(--rule);
  border-radius: var(--radius);
}
.dev-signin__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2);
  margin-top: var(--s-3);
}
</style>
